import { useState } from 'react';
import { submitEnrollment } from '../api/enroll';
import { useSite } from '../context/SiteContext';
import { normalizeBdPhone, toBn } from '../lib/utils';
import { CLOSED_LABEL, discountPercent, taka } from '../lib/price';
import { WALLETS, normalizeTrxId, normalizeWalletNumber, walletNames } from '../lib/payment';
import { SeatBar } from './Offer';
import { ArrowRightIcon, CheckIcon, CopyIcon, FacebookIcon, ShieldIcon, WhatsAppIcon } from './Icons';

const profiles = [
  { value: 'student', label: '🎓 শিক্ষার্থী' },
  { value: 'employee', label: '💼 চাকরিজীবী' },
  { value: 'other', label: '🙋 অন্যান্য' },
];

const FIELDS = ['name', 'phone', 'email', 'paymentMethod', 'paymentFrom', 'transactionId'];
const initial = { name: '', phone: '', email: '', profile: 'student', paymentMethod: '', paymentFrom: '', transactionId: '' };

// Mirrors the backend rules so most mistakes are caught before a round trip.
// `method` is the selected wallet, or null when no payment numbers are set (payment collected by phone).
function validate(values, method) {
  const errors = {};
  const name = values.name.trim();
  if (name.length < 2 || name.length > 100) errors.name = 'অনুগ্রহ করে আপনার পুরো নাম লিখুন';
  if (!normalizeBdPhone(values.phone)) errors.phone = 'সঠিক ১১ ডিজিটের মোবাইল নম্বর দিন (যেমন 01XXXXXXXXX)';
  if (values.email.trim() && !/^\S+@\S+\.\S+$/.test(values.email.trim())) errors.email = 'সঠিক ইমেইল ঠিকানা দিন';
  if (method) {
    if (!normalizeWalletNumber(values.paymentFrom, method)) errors.paymentFrom = 'যে নম্বর থেকে টাকা পাঠিয়েছেন সেটি সঠিকভাবে দিন (যেমন 01XXXXXXXXX)';
    if (!normalizeTrxId(values.transactionId)) {
      errors.transactionId = values.transactionId.trim() ? 'সঠিক ট্রানজেকশন আইডি (TrxID) দিন' : 'ট্রানজেকশন আইডি (TrxID) দিন';
    }
  }
  return errors;
}

// Carries ad-campaign tags (utm_source=facebook etc.) along with the lead.
function getUtmParams() {
  const params = new URLSearchParams(window.location.search);
  return Object.fromEntries([...params].filter(([k]) => k.startsWith('utm_')));
}

export default function EnrollForm() {
  const { course, payment } = useSite();
  return course.enrollmentOpen ? <OpenEnrollment course={course} payment={payment} /> : <ClosedEnrollment course={course} />;
}

function OpenEnrollment({ course, payment }) {
  const [values, setValues] = useState(initial);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [serverError, setServerError] = useState('');
  const [result, setResult] = useState(null);
  const discount = discountPercent(course);
  const fee = course.price != null ? taka(course.price) : 'কোর্স ফি';
  // Payment numbers arrive with /api/site, so the first wallet is the default until one is picked.
  const method = payment.some((p) => p.method === values.paymentMethod) ? values.paymentMethod : payment[0]?.method ?? null;
  const classStep = 'লাইভ ক্লাসে যোগ দিন';

  const steps = method
    ? ['নিচের ফর্মে আপনার তথ্য দিন', `দেখানো নম্বরে ${fee} Send Money করে TrxID লিখুন`, `পেমেন্ট যাচাইয়ের পর ${classStep}`]
    : ['নিচের ফর্মটি পূরণ করুন', `${fee} পেমেন্ট করুন`, classStep];

  const set = (field) => (e) => {
    setValues((v) => ({ ...v, [field]: e.target.value }));
    if (errors[field]) setErrors((er) => ({ ...er, [field]: undefined }));
  };

  async function handleSubmit(e) {
    e.preventDefault();
    const found = validate(values, method);
    setErrors(found);
    if (Object.keys(found).length) return;

    setStatus('submitting');
    setServerError('');
    try {
      const res = await submitEnrollment({
        name: values.name.trim(),
        phone: normalizeBdPhone(values.phone),
        email: values.email.trim() || null,
        profile: values.profile,
        batch: course.batchName,
        batchStart: course.batchStart,
        price: course.price,
        utm: getUtmParams(),
        submittedAt: new Date().toISOString(),
        ...(method && {
          paymentMethod: method,
          paymentFrom: normalizeWalletNumber(values.paymentFrom, method),
          transactionId: normalizeTrxId(values.transactionId),
        }),
      });
      // 201 = new, 200 + duplicate = already registered for this batch; both get the thank-you screen.
      setResult(res);
      setStatus('success');
    } catch (err) {
      if (err.fieldErrors) {
        setErrors(Object.fromEntries(FIELDS.filter((f) => err.fieldErrors[f]).map((f) => [f, err.fieldErrors[f]])));
      }
      setServerError(err.message);
      setStatus('error');
    }
  }

  return (
    <section id="enroll" className="bg-gradient-to-br from-word-600 via-word-700 to-word-900 py-20">
      <div className="container-x grid items-center gap-12 lg:grid-cols-2">
        <div className="text-white">
          <span className="eyebrow bg-white/10 text-cta-400">ভর্তি ফর্ম</span>
          <h2 className="section-title text-white">আজই আপনার সিট নিশ্চিত করুন</h2>
          <p className="mt-4 text-lg text-white/80">
            {method
              ? `ফর্মটি পূরণ করুন আর ${walletNames(payment)}-এ কোর্স ফি পাঠিয়ে ট্রানজেকশন আইডি দিন। পেমেন্ট যাচাই করে আমরা আপনার সিট নিশ্চিত করব।`
              : 'মাত্র ৩০ সেকেন্ডে ফর্মটি পূরণ করুন। আমাদের টিম আপনাকে কল করে পেমেন্ট ও ক্লাসের বিস্তারিত জানিয়ে দেবে।'}
          </p>
          <ol className="mt-8 space-y-4">
            {steps.map((step, i) => (
              <li key={step} className="flex items-center gap-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/15 text-lg font-bold">{toBn(i + 1)}</span>
                <span className="text-lg">{step}</span>
              </li>
            ))}
          </ol>
          <div className="mt-8 flex items-center gap-3 rounded-xl bg-white/10 p-4 text-white/90">
            <ShieldIcon className="h-8 w-8 shrink-0 text-cta-400" />
            <span>আপনার তথ্য সম্পূর্ণ নিরাপদ। শুধুমাত্র কোর্স সংক্রান্ত যোগাযোগে ব্যবহার করা হবে।</span>
          </div>
        </div>

        <div className="rounded-3xl bg-white p-6 shadow-2xl sm:p-8">
          {status === 'success' ? (
            <Success
              name={values.name.trim()}
              batch={result?.batch || course.batchName}
              whatsapp={course.contact.whatsapp}
              paid={method && { method, transactionId: normalizeTrxId(values.transactionId) }}
            />
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              <div className="flex items-baseline justify-between">
                <h3 className="text-2xl font-bold text-slate-900">রেজিস্ট্রেশন করুন</h3>
                {course.price != null && (
                  <div className="text-right">
                    <span className="text-2xl font-bold text-cta-500">{taka(course.price)}</span>
                    {discount && <span className="ml-2 text-slate-400 line-through">{taka(course.regularPrice)}</span>}
                  </div>
                )}
              </div>
              <div className="mt-4">
                <SeatBar />
              </div>

              <div className="mt-6 space-y-4">
                <Field label="আপনার নাম" required error={errors.name} id="name">
                  <input id="name" className="input" autoComplete="name" maxLength={100} placeholder="যেমন: মোঃ রাকিব হাসান" value={values.name} onChange={set('name')} />
                </Field>
                <Field label="মোবাইল নম্বর" required error={errors.phone} id="phone">
                  <input id="phone" className="input" type="tel" inputMode="tel" autoComplete="tel" placeholder="01XXXXXXXXX" value={values.phone} onChange={set('phone')} />
                </Field>
                <Field label="ইমেইল" hint="(ঐচ্ছিক)" error={errors.email} id="email">
                  <input id="email" className="input" type="email" autoComplete="email" placeholder="you@example.com" value={values.email} onChange={set('email')} />
                </Field>
                <fieldset>
                  <legend className="mb-1.5 font-medium text-slate-700">আপনি কে?</legend>
                  <div className="grid grid-cols-3 gap-2">
                    {profiles.map((p) => (
                      <label
                        key={p.value}
                        className={`cursor-pointer rounded-lg border-2 px-2 py-2.5 text-center text-sm font-semibold transition ${
                          values.profile === p.value ? 'border-word-500 bg-word-50 text-word-600' : 'border-slate-200 text-slate-600 hover:border-slate-300'
                        }`}
                      >
                        <input type="radio" name="profile" value={p.value} checked={values.profile === p.value} onChange={set('profile')} className="sr-only" />
                        {p.label}
                      </label>
                    ))}
                  </div>
                </fieldset>

                {method && <PaymentFields payment={payment} method={method} fee={fee} values={values} errors={errors} set={set} />}
              </div>

              {status === 'error' && serverError && (
                <p className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-600" role="alert">
                  {serverError}
                </p>
              )}

              <button type="submit" className="btn-cta mt-6 w-full disabled:cursor-wait disabled:opacity-70" disabled={status === 'submitting'}>
                {status === 'submitting' ? (
                  <>
                    <span className="h-5 w-5 animate-spin rounded-full border-2 border-white/40 border-t-white" /> পাঠানো হচ্ছে...
                  </>
                ) : (
                  <>
                    সিট বুক করুন <ArrowRightIcon className="h-5 w-5" />
                  </>
                )}
              </button>
              <p className="mt-3 text-center text-sm text-slate-500">
                {method ? 'পেমেন্ট যাচাই করে কল বা SMS-এ আপনার সিট নিশ্চিত করা হবে' : 'এখনই কোনো পেমেন্ট লাগবে না'}
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function PaymentFields({ payment, method, fee, values, errors, set }) {
  const wallet = WALLETS[method];
  const { number } = payment.find((p) => p.method === method);
  return (
    <fieldset className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
      <legend className="px-1 font-medium text-slate-700">
        পেমেন্ট <span className="text-red-500">*</span>
      </legend>
      {payment.length > 1 && (
        <div className="mb-4 grid gap-2" style={{ gridTemplateColumns: `repeat(${payment.length}, minmax(0, 1fr))` }}>
          {payment.map((p) => {
            const w = WALLETS[p.method];
            const selected = p.method === method;
            return (
              <label
                key={p.method}
                className={`cursor-pointer rounded-lg border-2 bg-white px-2 py-2.5 text-center font-bold transition ${selected ? '' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}
                style={selected ? { borderColor: w.color, color: w.color, backgroundColor: `${w.color}0d` } : undefined}
              >
                <input type="radio" name="paymentMethod" value={p.method} checked={selected} onChange={set('paymentMethod')} className="sr-only" />
                {w.name}
              </label>
            );
          })}
        </div>
      )}

      <div className="rounded-xl bg-white p-4 ring-1 ring-slate-200">
        <p className="text-sm text-slate-600">
          নিচের <strong>{wallet.name}</strong> পার্সোনাল নম্বরে <strong className="text-slate-900">{fee}</strong> Send Money করুন
        </p>
        <div className="mt-2 flex items-center justify-between gap-3">
          <span className="text-2xl font-bold tabular-nums tracking-wide" style={{ color: wallet.color }}>
            {number}
          </span>
          <CopyButton text={number} />
        </div>
        <p className="mt-2 text-sm text-slate-500">
          {wallet.name} অ্যাপ বা {wallet.ussd} ডায়াল করে “Send Money” বেছে নিন (“Payment” নয়)। টাকা পাঠানোর পর SMS-এ ট্রানজেকশন আইডি (TrxID) পাবেন।
        </p>
      </div>

      <div className="mt-4 space-y-4">
        <Field label="যে নম্বর থেকে টাকা পাঠিয়েছেন" required error={errors.paymentFrom} id="paymentFrom">
          <input
            id="paymentFrom"
            className="input"
            type="tel"
            inputMode="numeric"
            autoComplete="off"
            placeholder={method === 'rocket' ? '01XXXXXXXXXX' : '01XXXXXXXXX'}
            value={values.paymentFrom}
            onChange={set('paymentFrom')}
          />
        </Field>
        <Field label="ট্রানজেকশন আইডি (TrxID)" required error={errors.transactionId} id="transactionId">
          <input
            id="transactionId"
            className="input font-mono uppercase placeholder:font-sans placeholder:normal-case"
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            maxLength={40}
            placeholder="যেমন: 8N7A6D5S4F"
            value={values.transactionId}
            onChange={set('transactionId')}
          />
        </Field>
      </div>
      {errors.paymentMethod && <p className="mt-2 text-sm text-red-600">{errors.paymentMethod}</p>}
    </fieldset>
  );
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  if (!navigator.clipboard) return null;

  const copy = () =>
    navigator.clipboard.writeText(text).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      },
      () => {},
    );
  return (
    <button type="button" onClick={copy} className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50">
      {copied ? <CheckIcon className="h-4 w-4 text-excel-600" /> : <CopyIcon className="h-4 w-4" />}
      {copied ? 'কপি হয়েছে' : 'কপি'}
    </button>
  );
}

function ClosedEnrollment({ course }) {
  const { facebook, whatsapp } = course.contact;
  return (
    <section id="enroll" className="bg-gradient-to-br from-word-600 via-word-700 to-word-900 py-20">
      <div className="container-x">
        <div className="mx-auto max-w-xl rounded-3xl bg-white p-8 text-center shadow-2xl sm:p-10">
          <div className="text-5xl">🔒</div>
          <h2 className="mt-4 text-3xl font-bold text-slate-900">{CLOSED_LABEL}</h2>
          <p className="mt-3 text-lg text-slate-600">
            এই মুহূর্তে কোনো ব্যাচে ভর্তি নেওয়া হচ্ছে না। পরবর্তী ব্যাচ শুরু হলে আমাদের Facebook পেজে জানিয়ে দেওয়া হবে।
          </p>
          <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
            {facebook && (
              <a href={facebook} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#1877F2] px-6 py-3 font-bold text-white transition hover:brightness-95">
                <FacebookIcon className="h-5 w-5" /> Facebook পেজ ফলো করুন
              </a>
            )}
            {whatsapp && (
              <a href={`https://wa.me/${whatsapp}`} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#25D366] px-6 py-3 font-bold text-white transition hover:brightness-95">
                <WhatsAppIcon className="h-5 w-5" /> WhatsApp-এ জানুন
              </a>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, hint, required, error, id, children }) {
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block font-medium text-slate-700">
        {label} {required && <span className="text-red-500">*</span>} {hint && <span className="text-sm font-normal text-slate-400">{hint}</span>}
      </label>
      {children}
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
}

// `paid` = { method, transactionId } when the form included Send Money details.
function Success({ name, batch, whatsapp, paid }) {
  const trx = paid ? ` ${WALLETS[paid.method].name} TrxID: ${paid.transactionId}` : '';
  const waText = encodeURIComponent(`আসসালামু আলাইকুম, আমি ${name}। ${batch ? `${batch}-এ ` : ''}MS Excel কোর্সে রেজিস্ট্রেশন করেছি।${trx}`);

  return (
    <div className="py-6 text-center" role="status">
      <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-excel-100">
        <CheckIcon className="h-10 w-10 text-excel-600" />
      </div>
      <h3 className="mt-5 text-2xl font-bold text-slate-900">ধন্যবাদ, {name}! 🎉</h3>
      {paid ? (
        <>
          <p className="mt-3 text-slate-600">আপনার রেজিস্ট্রেশন ও পেমেন্টের তথ্য আমরা পেয়েছি। পেমেন্ট যাচাই করে শীঘ্রই কল বা SMS-এ আপনার সিট নিশ্চিত করা হবে।</p>
          <p className="mt-4 inline-block rounded-lg bg-slate-100 px-4 py-2 text-sm text-slate-700">
            {WALLETS[paid.method].name} · TrxID <strong className="font-mono">{paid.transactionId}</strong>
          </p>
        </>
      ) : (
        <p className="mt-3 text-slate-600">আপনার রেজিস্ট্রেশন আমরা পেয়েছি। শীঘ্রই আমাদের টিম আপনাকে কল করে পেমেন্ট ও ক্লাসের বিস্তারিত জানিয়ে দেবে।</p>
      )}
      {whatsapp && (
        <a href={`https://wa.me/${whatsapp}?text=${waText}`} target="_blank" rel="noreferrer" className="mt-6 inline-flex items-center gap-2 rounded-xl bg-[#25D366] px-6 py-3 font-bold text-white transition hover:brightness-95">
          <WhatsAppIcon className="h-5 w-5" /> WhatsApp-এ দ্রুত নিশ্চিত করুন
        </a>
      )}
    </div>
  );
}
